/**
 * 动态配置管理器
 * 负责远程配置获取、localStorage 缓存、版本管理、按需查询
 */

import { EventType } from '../types/event'

import type {
  DynamicConfig,
  DynamicPageConfig,
  DynamicElementConfig,
  DynamicEventConfig,
  CachedDynamicConfig,
  RemoteDynamicConfig,
  FieldSource,
} from '../types/dynamic-config'
import { localStorageAdapter } from '../utils/storage'
import { findMatchingPageId } from '../utils/route-matcher'
import { hashString } from '../utils/index'
import { getSignHeaders } from '../utils/sign'


/** 缓存 localStorage key */
const CACHE_KEY = 'xt_tracker_dynamic_config'

/** 远程请求超时时间（毫秒） */
const FETCH_TIMEOUT = 1000


/** 页面级事件类型 */
const PAGE_LEVEL_EVENT_TYPES: EventType[] = ['openView', 'closeView']

/**
 * 判断事件类型是否为页面级事件
 * @param eventType 事件类型
 * @returns 是否为页面级事件（openView/closeView）
 */
function isPageLevelEvent(eventType: string): boolean {
  return PAGE_LEVEL_EVENT_TYPES.includes(eventType as EventType)
}

/**
 * 将远程配置格式转换为本地配置格式
 * @param remote 远程配置（扁平 events 数组）
 * @returns 本地配置（pages → eles → events 层级结构）
 */
function transformRemoteToLocalConfig(remote: RemoteDynamicConfig): DynamicConfig {
  const pages: Record<string, DynamicPageConfig> = {}

  for (const event of remote.events) {
    const pageId = event.pagePath

    // 确保页面配置存在
    if (!pages[pageId]) {
      pages[pageId] = {
        pageId,
        pageName: event.pageName,
        businessName: event.businessName,
        eles: {},
      }
    }

    // 转换字段配置
    const fields = event.fields?.map(f => ({
      fieldKey: f.fieldKey,
      fieldSource: f.fieldSource as FieldSource,
      bindType: f.bindType,
      primaryApi: f.primaryApi,
      ...(f.apiUrl && { apiUrl: f.apiUrl }),
    }))

    // 构建事件配置
    const eventConfig: DynamicEventConfig = {
      eventType: event.eventType as EventType,
      eventCode: event.eventCode,
      eventName: event.eventName,
      ...(fields && fields.length > 0 && { fields }),
      // 映射 elementApiPath（点击后拦截 API 路径）
      ...(event.elementApiPath && { elementApiPath: event.elementApiPath }),
    }

    // 判断事件是页面级还是元素级
    if (isPageLevelEvent(event.eventType)) {
      // 页面级事件：映射到 eventMap
      if (!pages[pageId].eventMap) {
        pages[pageId].eventMap = {}
      }
      // 页面级事件：映射到 eventMap，目前只有 openView，closeView需要根据openView手动映射
      pages[pageId].eventMap[event.eventType] = eventConfig
      if (event.eventType === 'openView') {
        pages[pageId].eventMap['closeView'] = eventConfig
      }
    } else {
      // 元素级事件：添加到 eles
      const eleId = event.elementSelector

      // 必须有元素选择器
      if (!eleId) continue

      // 确保元素配置存在
      if (!pages[pageId].eles[eleId]) {
        pages[pageId].eles[eleId] = {
          eleId,
          eleName: event.elementName,
          eleBusinessName: event.elementBusinessName,
          events: [],
        }
      }

      pages[pageId].eles[eleId].events.push(eventConfig)
    }
  }
  return {
    appStatus: remote.appStatus,
    globalSamplingRate: remote.globalSamplingRate,
    versionNo: remote.versionNo,
    hasNewVersion: remote.hasNewVersion,
    pages,
  }
}

/**
 * 动态配置管理器
 */
export class DynamicConfigManager {
  /** 当前活跃配置 */
  private config: DynamicConfig | null = null
  /** 远程配置地址 */
  private configUrl: string
  /** 配置是否已成功加载 */
  private loaded = false
  /** 调试模式 */
  private debug: boolean
  /** 应用密钥 */
  private appKey: string
  /** 当前版本号（可选） */
  private appVersion?: string
  /** 是否启用签名 */
  private enableSign: boolean
  /** 签名秘钥 */
  private signSecret?: string
  /** 父容器应用 appKey 列表 */
  private containerAppKeys: string[]

  constructor(
    configUrl: string,
    appKey: string,
    appVersion?: string,
    debug = false,
    enableSign = true,
    signSecret?: string,
    containerAppKeys: string[] = []
  ) {
    this.configUrl = configUrl
    this.appKey = appKey
    this.appVersion = appVersion
    this.debug = debug
    this.enableSign = enableSign
    this.signSecret = signSecret
    this.containerAppKeys = containerAppKeys
  }

  /**
   * 主加载流程（缓存优先策略）
   * 1. 优先从本地缓存加载，保证配置立即可用
   * 2. 异步获取远程配置，版本不同时更新缓存并替换当前配置
   * 3. 远程失败时继续使用已加载的缓存配置
   * @returns 配置是否可用
   */
  async load(): Promise<boolean> {
    // 1. 优先从本地缓存加载，保证配置立即可用
    const cached = this.tryLoadFromCache()
    if (cached) {
      this.config = cached
      this.loaded = true
      this.log('从缓存加载配置', { versionNo: cached.versionNo })
    }

    // 2. 异步获取远程配置
    const remote = await this.fetchRemoteConfig()

    if (remote) {
      // 比对版本号，决定是否更新缓存
      if (!cached || cached.versionNo !== remote.versionNo) {
        this.saveToCache(remote)
        this.log('缓存已更新', {
          oldVersion: cached?.versionNo ?? '无缓存',
          newVersion: remote.versionNo,
        })
      }

      // 替换为远程最新配置
      this.config = remote
      this.loaded = true
      this.log('远程配置加载成功', { versionNo: remote.versionNo })
      return true
    }

    // 3. 远程失败时，如果已有缓存配置则继续使用
    if (this.loaded) {
      this.log('远程获取失败，继续使用缓存配置')
      return true
    }

    // 远程获取失败且无缓存
    this.log('动态配置加载失败：远程获取失败且无缓存')
    return false
  }

  /**
   * 从 Mock 数据直接加载（调试用）
   */
  loadFromMock(config: DynamicConfig): void {
    this.config = config
    this.loaded = true
    this.log('Config loaded from mock', { versionNo: config.versionNo })
  }

  /**
   * 从 localStorage 读取缓存
   */
  private tryLoadFromCache(): DynamicConfig | null {
    try {
      const raw = localStorageAdapter.getItem(CACHE_KEY)
      if (!raw) return null

      const cached: CachedDynamicConfig = JSON.parse(raw)

      // 校验 configUrl 是否匹配（防止不同环境串配置，使用哈希比对）
      if (cached.configUrl !== hashString(this.configUrl)) {
        this.log('Cache configUrl mismatch, ignoring cache')
        return null
      }

      // 校验基本结构
      if (!cached.config?.versionNo || !cached.config?.pages) {
        this.log('Cache data invalid, ignoring cache')
        return null
      }

      this.log('Cache loaded', { versionNo: cached.config.versionNo, cachedAt: cached.cachedAt })
      return cached.config
    } catch {
      this.log('Cache parse error, ignoring cache')
      return null
    }
  }

  /**
   * 从远程地址获取配置
   */
  private async fetchRemoteConfig(): Promise<DynamicConfig | null> {
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT)

      const requestBody = {
        appKey: this.appKey,
        appKeys: this.containerAppKeys, // 父容器应用 appKey 列表
      }

      // 构建请求头
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }

      // 如果启用签名，添加签名头
      if (this.enableSign) {
        const signHeaders = getSignHeaders(requestBody, {
          secret: this.signSecret,
          url: this.configUrl,
          debug: false,
        })
        Object.assign(headers, signHeaders)
      }

      const response = await fetch(this.configUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        this.log('Remote fetch failed', { status: response.status })
        return null
      }

      const res: { data: RemoteDynamicConfig } = await response.json()

      const remoteData: RemoteDynamicConfig = res.data || {}
      // 校验远程数据基本结构
      if (!remoteData.versionNo || !Array.isArray(remoteData.events)) {
        this.log('Remote config invalid structure')
        return null
      }

      // 将远程配置转换为本地配置格式
      const data = transformRemoteToLocalConfig(remoteData)

      // 基本结构校验
      if (!data.versionNo || !data.pages || typeof data.pages !== 'object') {
        this.log('Remote config invalid structure')
        return null
      }

      return data
    } catch (error) {
      this.log('Remote fetch error', error)
      return null
    }
  }

  /**
   * 写入缓存
   */
  private saveToCache(config: DynamicConfig): void {
    try {
      const cached: CachedDynamicConfig = {
        config,
        cachedAt: Date.now(),
        configUrl: hashString(this.configUrl), // 哈希存储，避免明文暴露
      }
      localStorageAdapter.setItem(CACHE_KEY, JSON.stringify(cached))
    } catch {
      this.log('缓存保存失败')
    }
  }

  // ── 查询 API ──

  /**
   * 配置是否可用
   */
  isReady(): boolean {
    return this.loaded && this.config !== null
  }

  /**
   * 获取当前配置
   */
  getConfig(): DynamicConfig | null {
    return this.config
  }

  /**
   * 获取页面配置
   * 精确匹配 pageId
   */
  getPageConfig(pageId: string): DynamicPageConfig | null {
    if (!this.config) return null

    const matchedPageId = findMatchingPageId(this.config.pages, pageId)
    if (!matchedPageId) return null

    return this.config.pages[matchedPageId] ?? null
  }

  /**
   * 判断页面是否在配置中
   */
  isPageConfigured(pageId: string): boolean {
    return this.getPageConfig(pageId) !== null
  }

  /**
   * 获取元素配置
   */
  getElementConfig(pageId: string, eleId: string): DynamicElementConfig | null {
    const pageConfig = this.getPageConfig(pageId)
    if (!pageConfig) return null
    return pageConfig.eles[eleId] ?? null
  }

  /**
   * 获取事件配置
   */
  getEventConfig(pageId: string, eleId: string, eventType: EventType): DynamicEventConfig | null {
    const eleConfig = this.getElementConfig(pageId, eleId)
    if (!eleConfig) return null
    return eleConfig.events.find(e => e.eventType === eventType) ?? null
  }

  /**
   * 判断是否应采集该事件
   */
  shouldTrackEvent(pageId: string, eleId: string, eventType: EventType): boolean {
    return this.getEventConfig(pageId, eleId, eventType) !== null
  }

  /**
   * 调试
   */
  private log(message: string, data?: unknown): void {
    if (this.debug) {
      console.log('%c[Tracker DynamicConfig] %c ' + message, 'background: #f3cc62; color: #fff;', 'color: inherit;', data)
    }
  }
}
